/**
 * Iter209 — SubscriptionOnboarding
 *
 * Pagina full-screen mostrata al primo accesso di un Pro Master
 * (professional senza parent_user_id) che non ha ancora scelto un
 * abbonamento (`subscription_onboarding_completed === false`).
 * L'utente NON può accedere alle altre pagine finché non conferma
 * una scelta.
 *
 * Sorgente piani: `GET /api/subscription-plans`.
 * Selezione:      `PUT /api/users/me/subscription` con `{plan_id}`.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle, Sparkle, Star, Warning, X } from "@phosphor-icons/react";

const fmtEur = (v) =>
  v === 0
    ? "€ 0"
    : `€ ${(Math.round(v * 100) / 100).toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

function PlanCard({ plan, active, onChoose, submitting, changeMode, isCurrent, currentFeatures, freeFeatures }) {
  const isFree = plan.id === "free";
  const isRecommended = plan.is_recommended;
  // Iter209ae — Le card dei piani superiori (Email/Sito/Tutto incluso)
  // mostrano PRIMA le funzionalità ereditate dal piano Gratuito (senza
  // badge "NUOVO"), POI le funzionalità aggiuntive del piano con badge
  // "NUOVO". Il piano Gratuito mostra solo le proprie feature senza
  // etichette. Questo rende esplicito che nulla si perde in un upgrade.
  const freeSet = new Set(freeFeatures || []);
  const inheritedList = isFree ? [] : (freeFeatures || []).filter((f) => !!f);
  const ownList = (plan.features || []).filter((f) => !freeSet.has(f));
  const displayList = isFree
    ? (plan.features || []).map((f) => ({ label: f, kind: "own" }))
    : [
        ...inheritedList.map((f) => ({ label: f, kind: "inherited" })),
        ...ownList.map((f) => ({ label: f, kind: "new" })),
      ];
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition-all ${
        isCurrent
          ? "border-slate-300 opacity-70"
          : isRecommended
          ? "border-purple-500 ring-2 ring-purple-200"
          : "border-slate-200 hover:border-slate-300"
      }`}
    >
      {isCurrent && (
        <span
          data-testid={`plan-badge-current-${plan.id}`}
          className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-md"
        >
          Piano attuale
        </span>
      )}
      {!isCurrent && isRecommended && (
        <span
          data-testid={`plan-badge-recommended-${plan.id}`}
          className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-md flex items-center gap-1"
        >
          <Star size={12} weight="fill" /> Più scelto
        </span>
      )}
      {!isCurrent && isFree && !isRecommended && plan.badge && (
        <span
          data-testid={`plan-badge-${plan.id}`}
          className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full border border-emerald-200 flex items-center gap-1"
        >
          <Sparkle size={12} weight="fill" /> {plan.badge}
        </span>
      )}
      <div className="mb-2">
        <h3 className="font-display text-xl font-bold text-slate-900">{plan.name}</h3>
        <p className="text-sm text-slate-500 mt-1 min-h-[42px]">{plan.description}</p>
      </div>
      <div className="my-3">
        <span className="font-display text-4xl font-bold text-slate-900">{fmtEur(plan.monthly)}</span>
        <span className="text-sm text-slate-500 ml-1">/ mese</span>
      </div>
      <ul className="space-y-2 mb-6 mt-2 flex-1">
        {displayList.map((item) => {
          const isNew = item.kind === "new";
          return (
            <li
              key={`${item.kind}-${item.label}`}
              data-testid={isNew ? `plan-feature-new-${plan.id}` : `plan-feature-${item.kind}-${plan.id}`}
              className={`text-sm flex gap-2 items-start rounded-md px-1.5 py-0.5 -mx-1.5 ${
                isNew ? "bg-emerald-50 text-emerald-900" : "text-slate-700"
              }`}
            >
              <CheckCircle
                size={16}
                weight="fill"
                className={`shrink-0 mt-0.5 ${isNew ? "text-emerald-600" : "text-emerald-500"}`}
              />
              <span className="flex-1">{item.label}</span>
              {isNew && (
                <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded self-start mt-0.5">
                  Nuovo
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        onClick={() => onChoose(plan.id)}
        disabled={submitting || isCurrent}
        data-testid={`plan-choose-${plan.id}`}
        className={`h-11 rounded-lg font-semibold text-sm transition-colors ${
          isCurrent
            ? "bg-slate-200 text-slate-500 cursor-not-allowed"
            : isRecommended
            ? "bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white hover:opacity-90"
            : "bg-slate-900 text-white hover:bg-slate-800"
        } ${submitting ? "opacity-60 cursor-wait" : ""}`}
      >
        {isCurrent
          ? "Piano attivo"
          : submitting && active === plan.id
          ? "Attivazione…"
          : "Scegli questo piano"}
      </button>
    </div>
  );
}

export default function SubscriptionOnboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const changeMode = searchParams.get("change") === "1";
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [chosen, setChosen] = useState(null);
  // Iter209d — Alert downgrade: quando la nuova scelta rimuove
  // funzionalità presenti nel piano attivo, si intercetta il choose per
  // mostrare un mini alert di conferma con la lista di cosa si perderà.
  const [pendingDowngrade, setPendingDowngrade] = useState(null); // { plan_id, plan_name, lost: string[] }

  useEffect(() => {
    api.get("/subscription-plans")
      .then((r) => setPlans(r.data?.plans || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const performSubscribe = async (plan_id) => {
    setSubmitting(true);
    setChosen(plan_id);
    try {
      const r = await api.put("/users/me/subscription", { plan_id });
      const updatedUser = r.data?.user;
      if (updatedUser && setUser) setUser(updatedUser);
      const planName = plans.find((p) => p.id === plan_id)?.name || plan_id;
      // Iter209ag — Post-conferma: apri subito il wizard corretto in base
      // al piano scelto. Se il pro ha già in produzione ciò che il piano
      // richiede, saltiamo il wizard e andiamo in dashboard.
      if (plan_id === "free") {
        toast.success(`Piano aggiornato a ${planName}`);
        navigate("/dashboard", { replace: true });
        return;
      }
      // Verifica "già configurato" via /me/production-state (best-effort).
      let alreadyDone = false;
      try {
        const st = await api.get("/me/production-state");
        const s = st.data || {};
        alreadyDone = (
          (plan_id === "email" && s.email_in_production) ||
          (plan_id === "site" && s.site_in_production) ||
          (plan_id === "all" && s.email_in_production && s.site_in_production)
        );
      } catch (_e) { /* silent: prosegui col wizard */ }
      if (alreadyDone) {
        toast.success(`Piano ${planName} attivato. La configurazione era già completa.`);
        navigate("/dashboard", { replace: true });
        return;
      }
      toast.success(`Piano ${planName} attivato. Ora configuriamo il tuo account.`);
      // Iter209ag — Per tutti e 3 i piani a pagamento la configurazione
      // parte dal wizard MyDomain (scelta dominio → summary → site config).
      // Il flag `plan=X` istruisce MyDomain a saltare la selezione piano.
      navigate(`/my-domain?plan=${plan_id}&stage=domain&auto=1`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore attivazione piano");
      setSubmitting(false);
      setChosen(null);
    }
  };

  const choose = (plan_id) => {
    if (submitting) return;
    // Iter209ae — L'alert di downgrade scatta SOLO per un vero
    // downgrade (target.monthly < current.monthly). Le funzionalità del
    // piano Gratuito sono considerate incluse in tutti i piani a
    // pagamento, quindi passare da Gratuito → superiore non fa mai
    // scattare l'avviso. Anche un cambio orizzontale o verso l'alto
    // non mostra il modale.
    if (changeMode) {
      const currentId = user?.subscription_plan || "free";
      if (plan_id !== currentId) {
        const currentPlan = plans.find((p) => p.id === currentId);
        const targetPlan = plans.find((p) => p.id === plan_id);
        if (currentPlan && targetPlan) {
          const isDowngrade = Number(targetPlan.monthly) < Number(currentPlan.monthly);
          if (isDowngrade) {
            // Le feature perse considerano ereditate quelle del piano
            // Gratuito: se il target è "free" o omette una feature
            // free, quella non è persa perché è inclusa di base in
            // tutti i piani.
            const freePlan = plans.find((p) => p.id === "free");
            const freeSet = new Set((freePlan?.features) || []);
            const targetSet = new Set(targetPlan.features || []);
            const lost = (currentPlan.features || [])
              .filter((f) => !targetSet.has(f) && !freeSet.has(f));
            if (lost.length > 0) {
              setPendingDowngrade({
                plan_id,
                plan_name: targetPlan.name,
                current_name: currentPlan.name,
                lost,
              });
              return;
            }
          }
        }
      }
    }
    performSubscribe(plan_id);
  };

  const confirmDowngrade = () => {
    const p = pendingDowngrade;
    setPendingDowngrade(null);
    if (p) performSubscribe(p.plan_id);
  };
  const cancelDowngrade = () => setPendingDowngrade(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-white" data-testid="subscription-onboarding-page">
      <div className="max-w-6xl mx-auto px-4 py-14">
        <div className="text-center mb-10">
          {changeMode ? (
            <div className="label-eyebrow text-purple-600">Cambia piano</div>
          ) : (
            <div className="label-eyebrow text-purple-600">Benvenuto{user?.first_name ? `, ${user.first_name}` : ""}</div>
          )}
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-3">
            {changeMode ? "Scegli un altro piano" : "Scegli il piano giusto per te"}
          </h1>
          <p className="text-base text-slate-500 mt-3 max-w-xl mx-auto">
            {changeMode
              ? "Il tuo piano attuale resta attivo finché non ne selezioni uno diverso."
              : "Puoi cambiarlo in qualsiasi momento dalle impostazioni."}
          </p>
          {changeMode && (
            <div
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium px-3 py-1.5"
              data-testid="change-mode-hint"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              Le funzionalità evidenziate in verde sono quelle in più rispetto al tuo piano attuale
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-16" data-testid="subscription-loading">
            Caricamento piani…
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center text-slate-500 py-16">Nessun piano disponibile.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="plans-grid">
            {(() => {
              const currentId = changeMode ? (user?.subscription_plan || "free") : null;
              const currentPlan = currentId ? plans.find((p) => p.id === currentId) : null;
              const currentFeatures = currentPlan ? new Set(currentPlan.features || []) : new Set();
              // Iter209ae — Base "Gratuito": elenco delle sue feature usato
              // dalle card dei piani superiori per mostrarle come ereditate.
              const freePlan = plans.find((p) => p.id === "free");
              const freeFeatures = (freePlan?.features) || [];
              return plans.map((p) => (
                <PlanCard
                  key={p.id}
                  plan={p}
                  active={chosen}
                  onChoose={choose}
                  submitting={submitting}
                  changeMode={changeMode}
                  isCurrent={changeMode && p.id === currentId}
                  currentFeatures={currentFeatures}
                  freeFeatures={freeFeatures}
                />
              ));
            })()}
          </div>
        )}
      </div>

      {/* Iter209d — Mini alert downgrade */}
      {pendingDowngrade && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="downgrade-title"
          data-testid="downgrade-alert"
        >
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 px-5 py-4 flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                <Warning size={20} weight="fill" className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  id="downgrade-title"
                  className="font-display text-lg font-bold text-amber-900"
                >
                  Attenzione: perderai delle funzionalità
                </h3>
                <p className="text-xs text-amber-800 mt-1">
                  Passando da <strong>{pendingDowngrade.current_name}</strong> a{" "}
                  <strong>{pendingDowngrade.plan_name}</strong> smetterai di avere accesso a:
                </p>
              </div>
              <button
                onClick={cancelDowngrade}
                type="button"
                aria-label="Chiudi"
                data-testid="downgrade-close"
                className="shrink-0 text-amber-700 hover:text-amber-900"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <ul
              className="px-5 py-4 space-y-2 max-h-64 overflow-y-auto"
              data-testid="downgrade-lost-list"
            >
              {pendingDowngrade.lost.map((f) => (
                <li
                  key={f}
                  className="flex gap-2 items-start text-sm text-slate-700"
                  data-testid="downgrade-lost-item"
                >
                  <X size={14} weight="bold" className="text-rose-500 shrink-0 mt-1" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end">
              <button
                type="button"
                onClick={cancelDowngrade}
                data-testid="downgrade-cancel"
                className="h-10 px-4 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={confirmDowngrade}
                data-testid="downgrade-confirm"
                className="h-10 px-4 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700"
              >
                Conferma cambio piano
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
